/**
 * Native Impower section "Vertragsverteilung" on the unit contract editor:
 * injects an "Inject" button that selects every remaining property account
 * as excluded. Already excluded accounts are left untouched (re-clicking a
 * selected Ant Design option would deselect it).
 *
 * @param {{ propertyHrId?: string | null, unitContractId?: number | null }} ctx
 */
const LUX_VERTRAGSVERTEILUNG_TITLE = 'Vertragsverteilung'
const LUX_VERTRAGSVERTEILUNG_TOOLBAR_ID = 'lux-vertragsverteilung-inject-toolbar'
const LUX_VERTRAGSVERTEILUNG_BTN_ID = 'lux-vertragsverteilung-inject-btn'
const LUX_VERTRAGSVERTEILUNG_BTN_LABEL = 'Alle Konten ausschließen'
const LUX_VERTRAGSVERTEILUNG_CLEAR_BTN_ID = 'lux-vertragsverteilung-clear-btn'
const LUX_VERTRAGSVERTEILUNG_CLEAR_BTN_LABEL = 'Ausschlüsse löschen'
const LUX_EXCLUDED_ACCOUNT_CONTRACT_PATH = '/services/pmp-accounting/api/v1/excluded-account-contract'
const LUX_ACCOUNTS_PATH = '/services/pmp-accounting/api/v1/accounting/accounts'

const luxVertragsverteilungTitleText = (value) =>
  String(value || '')
    .replace(/\s+/g, ' ')
    .trim()

const luxIsVertragsverteilungTitle = (value) => {
  const text = luxVertragsverteilungTitleText(value)
  if (!text) return false
  return text === LUX_VERTRAGSVERTEILUNG_TITLE || text.endsWith(LUX_VERTRAGSVERTEILUNG_TITLE)
}

const luxFlattenAccounts = (nodes, out = []) => {
  if (!Array.isArray(nodes)) return out
  for (const node of nodes) {
    if (!node || typeof node !== 'object') continue
    out.push(node)
    const children = []
      .concat(node.subAccounts || [])
      .concat(node.subaccounts || [])
      .concat(node.accounts || [])
      .concat(node.divisions || [])
      .concat(node.children || [])
    if (children.length) luxFlattenAccounts(children, out)
  }
  return out
}

const luxAccountNumericId = (value) => {
  if (value == null || value === '') return null
  if (typeof value === 'object') {
    // Exclusion rows use `id` as the row PK. Prefer the account foreign key.
    return luxAccountNumericId(
      value.excludedAccountId ?? value.accountId ?? value.value ?? value.key ?? value.id
    )
  }
  const text = String(value).trim()
  if (!/^\d+$/.test(text)) return null
  const num = Number(text)
  return Number.isFinite(num) && num > 0 ? num : null
}

const luxAccountCode = (value) => {
  if (value == null) return ''
  if (typeof value === 'object') {
    const direct = String(value.code || value.accountCode || value.accountHrId || '').trim()
    if (direct) return direct
    if (typeof value.value === 'string' && !/^\d+$/.test(value.value.trim())) {
      return value.value.trim().split(/\s+/)[0]
    }
    if (typeof value.label === 'string') {
      return value.label.trim().split(/\s+/)[0] || ''
    }
    return ''
  }
  const text = String(value).trim()
  if (/^\d+$/.test(text)) return ''
  return text.split(/\s+/)[0] || text
}

const luxIsSelectablePropertyAccount = (account) => {
  if (!account || typeof account !== 'object') return false
  if (luxAccountSelectionKeys(account).length === 0) return false
  if (account.leaf === false) return false
  if (account.leaf === true) return true
  const children = []
    .concat(account.subAccounts || [])
    .concat(account.subaccounts || [])
    .concat(account.accounts || [])
  return children.length === 0
}

const luxAccountTypeName = (account) => {
  const type = account?.accountType
  if (typeof type === 'string') return type
  if (type && typeof type === 'object') return String(type.accountType || '')
  return ''
}

/** Sachkonten Impower can show in Vertragsverteilung — not Debitoren/Bank/VAT. */
const luxIsVertragsverteilungAccount = (account) => {
  if (!luxIsSelectablePropertyAccount(account)) return false
  if (account.divisible === false) return false
  const linkedContractId = Number(account.unitContractId)
  if (Number.isInteger(linkedContractId) && linkedContractId > 0) return false
  const code = luxAccountCode(account)
  if (/^(2000|2001|2002|1000|1001|442)(\/|$)/.test(code)) return false
  const type = luxAccountTypeName(account)
  if (type && type !== 'EXPENSE') return false
  return true
}

const luxAccountSelectionKeys = (value) => {
  const keys = []
  const id = luxAccountNumericId(value)
  if (id != null) keys.push(`id:${id}`)
  const code = luxAccountCode(value)
  if (code) keys.push(`code:${code}`)
  return keys
}

const luxCollectSelectionKeys = (values) => {
  const keys = new Set()
  const list = Array.isArray(values) ? values : values == null || values === '' ? [] : [values]
  for (const value of list) {
    for (const key of luxAccountSelectionKeys(value)) keys.add(key)
  }
  return keys
}

const luxCollectExcludedAccountIds = (entries) => {
  const ids = new Set()
  for (const entry of Array.isArray(entries) ? entries : []) {
    const id = luxAccountNumericId(entry)
    if (id != null) ids.add(id)
  }
  return ids
}

/**
 * Union of already-selected exclusions + remaining available accounts.
 * Already selected keys are never added again (would toggle them off in the UI).
 */
const luxMergeExcludedAccountSelection = (alreadySelected, availableAccounts) => {
  const selectedKeys = luxCollectSelectionKeys(alreadySelected)
  const added = []
  for (const account of availableAccounts || []) {
    if (!luxIsVertragsverteilungAccount(account)) continue
    const keys = luxAccountSelectionKeys(account)
    if (keys.length === 0 || keys.some((key) => selectedKeys.has(key))) continue
    for (const key of keys) selectedKeys.add(key)
    added.push(account)
  }
  return { added, selectedKeys }
}

const luxNormalizeExcludedPayload = (data) => {
  if (Array.isArray(data)) return data
  if (data && typeof data === 'object') {
    if (Array.isArray(data.content)) return data.content
    if (Array.isArray(data.excludedAccountContracts)) return data.excludedAccountContracts
    if (
      data.excludedAccountId != null ||
      data.accountId != null ||
      data.unitContractId != null
    ) {
      return [data]
    }
  }
  return []
}

const luxExclusionRowId = (entry) => {
  if (!entry || typeof entry !== 'object') return null
  const num = Number(entry.id)
  return Number.isInteger(num) && num > 0 ? num : null
}

/** Impower DELETE body: `[5478]` — exclusion row PKs, not account ids. */
const luxBuildExcludedAccountDeleteIds = (entries) => {
  const ids = []
  const seen = new Set()
  for (const entry of Array.isArray(entries) ? entries : []) {
    const rowId = typeof entry === 'number' ? entry : luxExclusionRowId(entry)
    if (rowId == null || seen.has(rowId)) continue
    seen.add(rowId)
    ids.push(rowId)
  }
  return ids
}

/** Rows whose account is not among known property accounts (deleted / unknown). */
const luxInvalidExcludedRows = (exclusions, knownAccountIds) => {
  const known = knownAccountIds instanceof Set ? knownAccountIds : new Set()
  // An empty known set means the account list is missing or incomplete —
  // never treat every existing exclusion as junk (that would DELETE them all).
  if (known.size === 0) return []
  const rows = []
  const seen = new Set()
  for (const entry of Array.isArray(exclusions) ? exclusions : []) {
    const accountId = luxAccountNumericId(entry)
    if (accountId != null && known.has(accountId)) continue
    const rowId = luxExclusionRowId(entry)
    if (rowId == null || seen.has(rowId)) continue
    seen.add(rowId)
    rows.push({ rowId, accountId })
  }
  return rows
}

const luxIsDuplicateExcludedError = (result) => {
  if (!result || result.ok) return false
  const text = typeof result.data === 'string' ? result.data : JSON.stringify(result.data || '')
  return /duplicate key|already exists|unique constraint/i.test(text)
}

const luxFetchExcludedAccountContracts = async (token, unitContractId) => {
  const id = encodeURIComponent(String(unitContractId))
  const urls = [
    `${LUX_EXCLUDED_ACCOUNT_CONTRACT_PATH}?unitContractIds=${id}&size=5000&page=0`,
    `${LUX_EXCLUDED_ACCOUNT_CONTRACT_PATH}?unitContractIds=${id}`,
  ]
  let last = { ok: false, status: 0, data: null }
  for (const url of urls) {
    last = await luxVertragsverteilungJson(url, token)
    if (last.ok) return { ...last, entries: luxNormalizeExcludedPayload(last.data) }
  }
  return { ...last, entries: [] }
}

const luxDeleteExcludedAccountRows = async (token, entries) => {
  const ids = luxBuildExcludedAccountDeleteIds(entries)
  if (ids.length === 0) return { ok: true, deleted: 0 }
  const result = await luxVertragsverteilungJson(LUX_EXCLUDED_ACCOUNT_CONTRACT_PATH, token, {
    method: 'DELETE',
    body: JSON.stringify(ids),
  })
  if (result.ok || result.status === 204 || result.status === 404) {
    return { ok: true, deleted: ids.length }
  }
  return { ok: false, deleted: 0 }
}

const luxPruneInvalidExcludedRows = async (token, exclusions, knownAccountIds) => {
  const known = knownAccountIds instanceof Set ? knownAccountIds : new Set()
  if (known.size === 0) return { invalidRows: [], pruned: 0 }
  const invalidRows = luxInvalidExcludedRows(exclusions, known)
  if (invalidRows.length === 0) return { invalidRows, pruned: 0 }
  const deleted = await luxDeleteExcludedAccountRows(
    token,
    invalidRows.map((row) => ({ id: row.rowId }))
  )
  return { invalidRows, pruned: deleted.ok ? deleted.deleted : 0 }
}

const luxPostExcludedAccountCreateDtos = async (token, payload) => {
  if (payload.length === 0) return { ok: true, created: 0 }
  const bulk = await luxVertragsverteilungJson(LUX_EXCLUDED_ACCOUNT_CONTRACT_PATH, token, {
    method: 'POST',
    body: JSON.stringify(payload),
  })
  if (bulk.ok) return { ok: true, created: payload.length }
  if (!luxIsDuplicateExcludedError(bulk)) return { ok: false, created: 0 }
  let created = 0
  for (const row of payload) {
    const one = await luxVertragsverteilungJson(LUX_EXCLUDED_ACCOUNT_CONTRACT_PATH, token, {
      method: 'POST',
      body: JSON.stringify([row]),
    })
    if (one.ok) created += 1
    else if (!luxIsDuplicateExcludedError(one)) return { ok: false, created }
  }
  return { ok: true, created }
}

const luxBuildExcludedAccountCreateDtos = (accounts, unitContractId, alreadyExcludedIds) => {
  const skip = alreadyExcludedIds instanceof Set ? alreadyExcludedIds : new Set()
  const payload = []
  const seen = new Set()
  for (const account of accounts || []) {
    const accountId = luxAccountNumericId(account)
    if (accountId == null || skip.has(accountId) || seen.has(accountId)) continue
    seen.add(accountId)
    payload.push({ unitContractId, excludedAccountId: accountId })
  }
  return payload
}

const luxFindVertragsverteilungSection = (root) => {
  const scope = root || (typeof document !== 'undefined' ? document : null)
  if (!scope?.querySelectorAll) return null

  const byTestId =
    scope.querySelector('[data-testid="primary-section-wrapper-Vertragsverteilung"]') ||
    scope.querySelector('[data-testid="primary-section-wrapper-ContractDistribution"]') ||
    scope.querySelector('[data-testid="secondary-section-wrapper-Vertragsverteilung"]')
  if (byTestId) return byTestId

  const titled = scope.querySelectorAll(
    '.section-title, .sub-section-title, .section-title-box, [data-testid*="Vertragsverteilung"]'
  )
  for (const titleEl of titled) {
    if (!luxIsVertragsverteilungTitle(titleEl.textContent)) continue
    return (
      titleEl.closest('.PrimarySectionWrapper') ||
      titleEl.closest('[data-testid^="primary-section-wrapper"]') ||
      titleEl.closest('[data-testid^="secondary-section-wrapper"]') ||
      titleEl.closest('.sub-section') ||
      titleEl.closest('.section-wrapper-content')?.parentElement ||
      titleEl.closest('.section-number-box') ||
      titleEl.parentElement
    )
  }
  return null
}

const luxVertragsverteilungAuthToken = () => {
  if (typeof Lux_ApiClient_getAuthToken === 'function') return Lux_ApiClient_getAuthToken()
  try {
    const raw = localStorage.getItem('auth')
    return raw ? JSON.parse(raw).token : null
  } catch {
    return null
  }
}

const luxVertragsverteilungJson = async (url, token, options = {}) => {
  const response = await fetch(url, {
    ...options,
    headers: {
      Authorization: `Bearer ${token}`,
      Accept: 'application/json',
      ...(options.body ? { 'Content-Type': 'application/json' } : {}),
      ...(options.headers || {}),
    },
  })
  const text = await response.text()
  let data = null
  if (text) {
    try {
      data = JSON.parse(text)
    } catch {
      data = text
    }
  }
  return { ok: response.ok, status: response.status, data }
}

const luxNormalizeAccountsPayload = (data) => {
  if (Array.isArray(data)) return data
  if (data && typeof data === 'object') {
    if (Array.isArray(data.content)) return data.content
    if (Array.isArray(data.accounts)) return data.accounts
  }
  return []
}

/**
 * `available` is what exclude-all may POST. `knownAccountIds` is every account
 * id on the property — including rows the client filter skips — so prune does
 * not delete existing exclusions of Debitoren, linked, or non-EXPENSE accounts.
 */
const luxPartitionVertragsverteilungAccounts = (data) => {
  const flattened = luxFlattenAccounts(luxNormalizeAccountsPayload(data))
  return {
    available: flattened.filter(luxIsVertragsverteilungAccount),
    knownAccountIds: luxCollectExcludedAccountIds(flattened),
  }
}

const luxLoadValidVertragsverteilungAccounts = async (token, propertyHrId) => {
  const accountsUrl = `${LUX_ACCOUNTS_PATH}?accountCodeRegex=${encodeURIComponent('.*')}&propertyHrIds=${encodeURIComponent(propertyHrId)}`
  const accountsRes = await luxVertragsverteilungJson(accountsUrl, token)
  if (!accountsRes.ok) return { ok: false, available: [], knownAccountIds: new Set() }
  return { ok: true, ...luxPartitionVertragsverteilungAccounts(accountsRes.data) }
}

const luxStyleVertragsverteilungButton = (button, variant) => {
  button.type = 'button'
  button.className = 'ant-btn ant-btn-default Button'
  button.style.margin = '0'
  if (variant === 'clear') {
    button.style.backgroundColor = '#fff'
    button.style.borderColor = '#000'
    button.style.color = '#000'
  } else {
    button.style.backgroundColor = '#000'
    button.style.borderColor = '#000'
    button.style.color = '#fff'
  }
}

/** Latest inject ctx — click handlers and the unfold observer must not close over the first call. */
let luxVertragsverteilungLiveCtx = { propertyHrId: '', unitContractId: null }

const luxVertragsverteilungCtxSnapshot = (ctx) => ({
  propertyHrId: ctx?.propertyHrId || '',
  unitContractId: ctx?.unitContractId ?? null,
})

const luxVertragsverteilungCtxToken = (ctx) => {
  const unitContractId = ctx?.unitContractId
  return `${ctx?.propertyHrId || ''}::${unitContractId == null ? '' : unitContractId}`
}

const injectVertragsverteilungExcludeAll = (ctx) => {
  luxVertragsverteilungLiveCtx = luxVertragsverteilungCtxSnapshot(ctx)

  const editor =
    document.querySelector('.TenantContractEditor') || document.querySelector('.OwnerContractEditor')
  if (!editor) return false

  const section = luxFindVertragsverteilungSection(editor)
  if (!section) return false

  const ctxToken = luxVertragsverteilungCtxToken(luxVertragsverteilungLiveCtx)
  const existingToolbar = section.querySelector(`#${LUX_VERTRAGSVERTEILUNG_TOOLBAR_ID}`)
  if (existingToolbar) {
    if (existingToolbar.dataset.luxVvCtx === ctxToken) return true
    existingToolbar.remove()
  }

  if (section.dataset.luxVvExcludeWatch !== '1') {
    section.dataset.luxVvExcludeWatch = '1'
    const observer = new MutationObserver(() => {
      if (section.querySelector(`#${LUX_VERTRAGSVERTEILUNG_TOOLBAR_ID}`)) {
        observer.disconnect()
        return
      }
      injectVertragsverteilungExcludeAll(luxVertragsverteilungLiveCtx)
    })
    observer.observe(section, { childList: true, subtree: true })
  }

  // Mount in the foldable body so the button is hidden while Vertragsverteilung is collapsed.
  // If Impower failed to load the section, still mount so the user can repair exclusions.
  const content =
    section.querySelector('.section-content') || section.querySelector('.sub-section-content')
  const loadFailed = /Vertragsverteilung konnte nicht geladen werden/i.test(
    `${section.textContent || ''} ${editor.textContent || ''}`
  )
  const mount = content || (loadFailed ? section : null)
  if (!mount) return false

  const toolbar = document.createElement('div')
  toolbar.id = LUX_VERTRAGSVERTEILUNG_TOOLBAR_ID
  toolbar.style.display = 'flex'
  toolbar.style.flexWrap = 'wrap'
  toolbar.style.gap = '8px'
  toolbar.style.margin = '0 0 12px 0'

  const button = document.createElement('button')
  button.id = LUX_VERTRAGSVERTEILUNG_BTN_ID
  button.textContent = LUX_VERTRAGSVERTEILUNG_BTN_LABEL
  luxStyleVertragsverteilungButton(button, 'exclude')

  const clearButton = document.createElement('button')
  clearButton.id = LUX_VERTRAGSVERTEILUNG_CLEAR_BTN_ID
  clearButton.textContent = LUX_VERTRAGSVERTEILUNG_CLEAR_BTN_LABEL
  luxStyleVertragsverteilungButton(clearButton, 'clear')

  toolbar.dataset.luxVvCtx = ctxToken
  toolbar.dataset.unitContractId =
    luxVertragsverteilungLiveCtx.unitContractId == null
      ? ''
      : String(luxVertragsverteilungLiveCtx.unitContractId)
  toolbar.dataset.propertyHrId = luxVertragsverteilungLiveCtx.propertyHrId
  toolbar.appendChild(button)
  toolbar.appendChild(clearButton)
  mount.insertBefore(toolbar, mount.firstChild)

  const setBusy = (busy, label) => {
    button.disabled = busy
    clearButton.disabled = busy
    button.textContent = busy && label === 'exclude' ? 'Wird ausgewählt…' : LUX_VERTRAGSVERTEILUNG_BTN_LABEL
    clearButton.textContent =
      busy && label === 'clear' ? 'Wird gelöscht…' : LUX_VERTRAGSVERTEILUNG_CLEAR_BTN_LABEL
  }

  button.addEventListener('click', async (event) => {
    event.preventDefault()
    event.stopPropagation()
    if (button.disabled) return

    const propertyHrId = luxVertragsverteilungLiveCtx.propertyHrId || ''
    const unitContractId = luxVertragsverteilungLiveCtx.unitContractId ?? null
    const token = luxVertragsverteilungAuthToken()
    if (!token) {
      if (typeof showToast === 'function') showToast('Keine Anmeldung gefunden.', 'error')
      return
    }
    if (!propertyHrId) {
      if (typeof showToast === 'function') showToast('Objekt konnte nicht ermittelt werden.', 'error')
      return
    }

    setBusy(true, 'exclude')
    try {
      if (unitContractId == null) {
        throw new Error('Vertrags-ID fehlt; Konten konnten nicht gespeichert werden.')
      }

      const accounts = await luxLoadValidVertragsverteilungAccounts(token, propertyHrId)
      if (!accounts.ok) throw new Error('Konten konnten nicht geladen werden.')
      const { available, knownAccountIds } = accounts

      const excludedRes = await luxFetchExcludedAccountContracts(token, unitContractId)
      if (!excludedRes.ok) {
        throw new Error('Bereits ausgeschlossene Konten konnten nicht geladen werden.')
      }
      const alreadySelected = excludedRes.entries
      const { pruned } = await luxPruneInvalidExcludedRows(token, alreadySelected, knownAccountIds)

      const alreadyExcludedIds = luxCollectExcludedAccountIds(alreadySelected)
      const { added } = luxMergeExcludedAccountSelection(alreadySelected, available)
      const payload = luxBuildExcludedAccountCreateDtos(added, unitContractId, alreadyExcludedIds)

      if (payload.length === 0 && pruned === 0) {
        if (typeof showToast === 'function') {
          showToast(
            available.length === 0
              ? 'Keine Sachkonten gefunden. Bestehende Ausschlüsse wurden nicht geändert.'
              : 'Alle verfügbaren Konten sind bereits ausgeschlossen.',
            'info'
          )
        }
        return
      }

      const posted = await luxPostExcludedAccountCreateDtos(token, payload)
      if (!posted.ok) {
        throw new Error('Konten konnten nicht ausgeschlossen werden.')
      }

      const created = posted.created
      if (typeof showToast === 'function') {
        if (created > 0) {
          showToast(
            created === 1 ? '1 Konto ausgeschlossen.' : `${created} Konten ausgeschlossen.`,
            'success'
          )
        } else {
          showToast('Ungültige Ausschlüsse wurden entfernt.', 'success')
        }
      }
      window.setTimeout(() => window.location.reload(), 400)
    } catch (error) {
      console.error('Lux Inject: Vertragsverteilung', error)
      if (typeof showToast === 'function') {
        showToast(error?.message || 'Konten konnten nicht ausgewählt werden.', 'error')
      }
    } finally {
      setBusy(false)
    }
  })

  clearButton.addEventListener('click', async (event) => {
    event.preventDefault()
    event.stopPropagation()
    if (clearButton.disabled) return

    const unitContractId = luxVertragsverteilungLiveCtx.unitContractId ?? null
    const token = luxVertragsverteilungAuthToken()
    if (!token) {
      if (typeof showToast === 'function') showToast('Keine Anmeldung gefunden.', 'error')
      return
    }
    if (unitContractId == null) {
      if (typeof showToast === 'function') {
        showToast('Vertrags-ID fehlt; Ausschlüsse konnten nicht gelöscht werden.', 'error')
      }
      return
    }

    setBusy(true, 'clear')
    try {
      const excludedRes = await luxFetchExcludedAccountContracts(token, unitContractId)
      if (!excludedRes.ok) {
        throw new Error('Ausgeschlossene Konten konnten nicht geladen werden.')
      }
      const ids = luxBuildExcludedAccountDeleteIds(excludedRes.entries)
      if (ids.length === 0) {
        if (typeof showToast === 'function') {
          showToast('Keine Ausschlüsse vorhanden.', 'info')
        }
        return
      }

      const deleted = await luxDeleteExcludedAccountRows(token, ids)
      if (!deleted.ok) {
        throw new Error('Ausschlüsse konnten nicht gelöscht werden.')
      }
      if (typeof showToast === 'function') {
        showToast(
          deleted.deleted === 1
            ? '1 Ausschluss gelöscht.'
            : `${deleted.deleted} Ausschlüsse gelöscht.`,
          'success'
        )
      }
      window.setTimeout(() => window.location.reload(), 400)
    } catch (error) {
      console.error('Lux Inject: Vertragsverteilung clear', error)
      if (typeof showToast === 'function') {
        showToast(error?.message || 'Ausschlüsse konnten nicht gelöscht werden.', 'error')
      }
    } finally {
      setBusy(false)
    }
  })

  return true
}

if (typeof module !== 'undefined' && module.exports) {
  module.exports = {
    LUX_VERTRAGSVERTEILUNG_TITLE,
    LUX_VERTRAGSVERTEILUNG_TOOLBAR_ID,
    LUX_VERTRAGSVERTEILUNG_BTN_ID,
    LUX_VERTRAGSVERTEILUNG_BTN_LABEL,
    LUX_VERTRAGSVERTEILUNG_CLEAR_BTN_ID,
    LUX_VERTRAGSVERTEILUNG_CLEAR_BTN_LABEL,
    luxFlattenAccounts,
    luxIsSelectablePropertyAccount,
    luxIsVertragsverteilungAccount,
    luxPartitionVertragsverteilungAccounts,
    luxInvalidExcludedRows,
    luxIsDuplicateExcludedError,
    luxAccountSelectionKeys,
    luxMergeExcludedAccountSelection,
    luxBuildExcludedAccountCreateDtos,
    luxBuildExcludedAccountDeleteIds,
    luxNormalizeExcludedPayload,
    luxFindVertragsverteilungSection,
    injectVertragsverteilungExcludeAll,
  }
}
